import React from 'react';
import { Card, Form, Row, Col, OverlayTrigger, Tooltip, Button } from 'react-bootstrap-5';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { setOffMarketHoverPropertyId } from 'bundles/connect/components/store/actions/ui';
import { setOffMarketPropertyIdPayload } from 'bundles/connect/components/store/actions/off_market_property';
import OffMarketPropertyImage from '../search/components/results/off_market_property_image';
import { faBolt, faTrash } from '@fortawesome/pro-regular-svg-icons';
import type { CallListItem } from 'bundles/connect/types';
import { useDispatch, useSelector } from 'react-redux';
import CallListOffMarketSummary from './call_list_off_market_summary';
import { CALL_CONSOLE, callListUrl, deleteCallListItem } from 'bundles/connect/helpers/prospect_api';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import { selectCallListId } from '../store/selectors/call_console';
import { selectCallListItems, selectPage, selectTotalPages } from '../store/selectors/results';
import { setCallListItems, setPage, setTotalPages } from '../store/actions/results';
import { setCallListItemId } from '../store/actions/call_console';

type CallListOffMarketResultCardProps = {
  callListItem: CallListItem;
  isSelected: boolean;
  onSelectToggle: (callListItemId: number) => void;
  lastUpdated?: string;
}

export default function CallListOffMarketResultCard({
  callListItem,
  isSelected,
  onSelectToggle,
  lastUpdated
}: CallListOffMarketResultCardProps) {
  const dispatch = useDispatch();
  const callListItems = useSelector(selectCallListItems);
  const offMarketHoverPropertyId = useSelector((state: any) => state.ui.offMarketHoverPropertyId);
  const callListId = useSelector(selectCallListId);
  const page = useSelector(selectPage);
  const totalPages = useSelector(selectTotalPages);
  const isHovering = callListItem.insightsPropertyId == offMarketHoverPropertyId;

  const onMouseEnter = () => {
    dispatch(setOffMarketHoverPropertyId(callListItem.insightsPropertyId));
  };

  const onMouseLeave = () => {
    dispatch(setOffMarketHoverPropertyId());
  };

  const onClick = () => {
    onMouseLeave();
    dispatch(setOffMarketPropertyIdPayload(callListItem.insightsPropertyId || ''));
    dispatch(setCallListItemId(callListItem.id));
  };

  const handleSelectToggle = () => onSelectToggle(callListItem.id);

  const handleRemoveFromList = () => {
    if (confirm('Are you sure?')) {
      deleteCallListItem(callListItem.id).then(() => {
        const newItems = callListItems.filter(item => item.id !== callListItem.id);
        dispatch(setCallListItems(newItems));
        if (newItems.length === 0 && page > 1) {
          dispatch(setPage(page - 1));
          dispatch(setTotalPages(totalPages - 1));
        }
        displayToast('Item successfully removed from list', 'success');
      });
    }
  };

  return (
    <Card
      className={classNames('mb-3 overflow-hidden shadow', {
        'border-primary': isHovering,
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Row className="g-0 align-items-stretch">
        <Col className="d-flex flex-column" onClick={onClick}>
          <div className="d-flex flex-grow-1">
            <div className="py-3 ps-2" onClick={e => e.stopPropagation()}>
              <Form.Check
                checked={isSelected}
                data-pendo-tag="select_property_result_card"
                id={`property_${callListItem.insightsPropertyId}_checkbox`}
                type="checkbox"
                onChange={handleSelectToggle}
              />
            </div>
            <Card.Body className="p-2 clickable">
              <CallListOffMarketSummary callListItem={callListItem} lastUpdated={lastUpdated} />
            </Card.Body>
          </div>
        </Col>
        <Col
          className="map-result-thumb off-market-result-thumb d-none d-md-block clickable p-2"
          md={{ order: 'last', span: 'auto' }}
          xs={{ order: 'first', span: '12' }}
          onClick={onClick}
        >
          <OffMarketPropertyImage result={callListItem.callListable} />
        </Col>
        <Col
          className="d-flex flex-column border-start"
          xs={{ order: 'last', span: 'auto' }}
        >
          <div className="flex-fill clickable border-bottom" onClick={() => {
            window.location.href = `${callListUrl(CALL_CONSOLE, callListId)}?item_id=${callListItem.id}`;
          }}
          >
            <OverlayTrigger
              overlay={<Tooltip>{callListItem.viewedAt ? 'Resume' : 'Start'} Outreach</Tooltip>}
              placement="left"
            >
              <Button variant="text-primary h-100">
                <FontAwesomeIcon
                  icon={faBolt}
                />
              </Button>
            </OverlayTrigger>
          </div>
          <div className="flex-fill clickable" onClick={handleRemoveFromList}>
            <OverlayTrigger
              overlay={<Tooltip>Remove from List</Tooltip>}
              placement="left"
            >
              <Button variant="text-primary h-100">
                <FontAwesomeIcon
                  icon={faTrash}
                />
              </Button>
            </OverlayTrigger>
          </div>
        </Col>
      </Row>
    </Card>
  );
}
